import { useEffect, useState } from 'react';
import { IAppProps } from '@interfaces/App.interface';
import actStore from '@flux/actions/actStore';
import act from '@flux/actions/actView';

import Locations from '@pages/DataBase/Flavors/Locations/Locations';
import { Grid, Modal } from '@ts/storybook';
import LocationRules from '@pages/DataBase/Flavors/Locations/LocationRules';
import Readings from '@pages/DataBase/Readings/Readings';
import Items from '@pages/DataBase/Flavors/Items/Items';

import { CElement, CEntity, IElement } from '@ts/ui-models';
import GeometryDrawer from '@components/Maps/Map/GeometryDrawer';
import Events from '@pages/DataBase/Events/Events';
import { createLocRuleFromAllocationCheck, IAllocationCheck } from '@ts/ui-models';
import { CMapElement } from '@models/maps/Map';
import { useAppDispatch } from '@redux/store';
import { getGroupById } from '@services/DataBase/Process.service';

interface Action {
  id: string;
  action?:
    | 'openModalReading'
    | 'openModalMap'
    | 'openModalEvent'
    | 'openModalLocation'
    | 'openModalLocationRules'
    | 'openModalItemsContentProcess'
    | 'openModalExtraComp';
  row: CElement;
  title?: string;
  itemsFetchUrl?: string;
  filters?: any;
  component?: any;
  propsComponent?: any;
}

interface QuickAccessModalProps extends IAppProps {
  isModal?: boolean;
}

const QuickAccessModal = ({ isModal, ...props }: QuickAccessModalProps) => {
  const [showTableModal, setShowTableModal] = useState<Action['action']>(undefined);
  const [action, setAction] = useState<Action | undefined>(undefined);
  const [tableProps, setTableProps] = useState<any>(null);
  const dispatch = useAppDispatch();

  //#region Handlers
  const onClose = () => {
    setShowTableModal(undefined);
    actStore.setActionTableV3({ action: undefined });
  };
  //#endregion

  //#region Functions
  const fillTableProps = async () => {
    if (action) {
      switch (action.action) {
        case 'openModalReading':
          setTableProps({
            title: action.title ?? ['Readings of item', ' - ', action.row.getName()],
            itemsFetchUrl: action.itemsFetchUrl ?? `/api/v2/item/${action.row.getID()}/readings`,
            filters: action.filters,
          });
          break;
        case 'openModalMap':
          const tElement = new CElement(action.row);
          const othersGeometries = tElement.getCurrentLocations;

          setTableProps({
            title: ['Map for item', ' - ', tElement.getName()],
            element: tElement,
            othersGeometries: othersGeometries,
          });
          break;
        case 'openModalEvent':
          const element: CMapElement | CElement = action.row instanceof CMapElement ? action.row : new CElement(action.row);

          setTableProps({
            title: ['Events for item', ' - ', element.getName()],
            tElement: element,
          });
          break;
        case 'openModalLocation':
          setTableProps({
            title: ['Location for event', ' - ', action.row.getName()],
            where: { logicalId: action.id },
          });
          break;
        case 'openModalLocationRules':
          const listAllocationCheck: IAllocationCheck = action.row as any;
          setTableProps({
            title: ['Location Rules', ' - ', action.row.getName()],
            mode: 'view',
            values: {
              locationRules: listAllocationCheck.results.map(result => createLocRuleFromAllocationCheck(result)),
            },
          });
          break;
        case 'openModalItemsContentProcess':
          const group = await getGroupById(dispatch, action.propsComponent.groupId);
          if (group) {
            setTableProps({
              title: ['Contents', ' - ', action.row.getName()],
              configComponent: {
                componentId: 'processesItemsContent',
                where: { logicalId: group.items?.map(item => item.item.id) ?? [] },
              },
            });
          } else {
            setShowTableModal(undefined);
            actStore.setActionTableV3({ action: undefined });
          }
          break;
        case 'openModalExtraComp':
          setTableProps({
            isExternComponent: true,
            title: action.title || action.propsComponent.title,
          });
          break;
        default:
          break;
      }
    }
  };
  //#endregion

  useEffect(() => {
    const action = props.data.getIn(['action', 'data'])?.toJS();

    if (action && showTableModal !== action.action) {
      setAction(action);

      act.deleteResource('action');
    }
  }, [props.data.getIn(['action', 'data'])]);

  useEffect(() => {
    if (action) {
      fillTableProps();
      setShowTableModal(action.action);
    }
  }, [action]);

  if (isModal) return null;
  if (!tableProps) return null;
  const inheritedProps = { isModal: true, isQuickAccess: true };

  if (tableProps.isExternComponent && action) {
    const ExternComponent = action.component;

    return (
      <Modal
        withHeader
        title={tableProps.title}
        open={!!showTableModal}
        onClose={onClose}
        labelCancel="Close"
        maxWidth="xl"
        //resizable
      >
        <Grid container style={{ height: '500px', marginRight: -24, marginLeft: -24 }}>
          <ExternComponent {...action.propsComponent} isModal inheritedProps={inheritedProps} />
        </Grid>
      </Modal>
    );
  }

  return (
    <Modal
      withHeader
      title={tableProps.title}
      open={!!showTableModal}
      onClose={onClose}
      labelCancel="Close"
      fullWidth
      maxWidth="xl"
      //resizable
    >
      <Grid container className="h-[500px] -mx-6 -mt-2.5 -mb-[18px]">
        {action?.action === 'openModalReading' && (
          <Readings {...props} {...tableProps} inheritedProps={inheritedProps} isQuickAccess />
        )}
        {action?.action === 'openModalMap' && (
          <GeometryDrawer {...props} {...tableProps} inheritedProps={inheritedProps} isQuickAccess />
        )}
        {action?.action === 'openModalEvent' && (
          <Events {...props} tElement={tableProps.tElement} inheritedProps={inheritedProps} />
        )}
        {action?.action === 'openModalLocation' && <Locations {...tableProps} inheritedProps={inheritedProps} isQuickAccess />}
        {action?.action === 'openModalItemsContentProcess' && (
          <Items {...tableProps} inheritedProps={inheritedProps} isQuickAccess />
        )}
        {action?.action === 'openModalLocationRules' && (
          <LocationRules {...props} {...tableProps} inheritedProps={inheritedProps} isQuickAccess />
        )}
      </Grid>
    </Modal>
  );
};

export default QuickAccessModal;
